import { Component } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from '../core/auth.service';

interface NavItem {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  template: `
    <div class="saas-shell" [class.mobile-nav-open]="isMobileNavOpen">
      <button
        type="button"
        class="saas-mobile-overlay"
        [class.visible]="isMobileNavOpen"
        aria-label="Close navigation"
        (click)="closeMobileNav()"></button>

      <aside class="saas-sidebar" [class.is-mobile-open]="isMobileNavOpen" id="mobile-navigation">
        <div class="saas-brand">
          <img src="assets/perks-logo.svg" alt="PERKS Sports Academy">
          <div>
            <strong>PERKS</strong>
            <span>Sports Academy</span>
          </div>
          <button type="button" class="saas-mobile-close" aria-label="Close menu" (click)="closeMobileNav()">
            <span></span>
            <span></span>
          </button>
        </div>

        <nav class="saas-nav" aria-label="Main navigation">
          @for (item of navItems; track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
              (click)="closeMobileNav()">
              <span><svg aria-hidden="true"><use [attr.href]="'#icon-' + item.icon"></use></svg></span>
              {{ item.label }}
            </a>
          }
        </nav>

        <div class="saas-sidebar-footer">
          <span>Operating Day</span>
          <strong>{{ today }}</strong>
          <button type="button">New Session</button>
        </div>
      </aside>

      <main class="saas-main">
        <header class="saas-topbar">
          <button
            type="button"
            class="saas-mobile-menu"
            aria-label="Open navigation"
            [attr.aria-expanded]="isMobileNavOpen"
            aria-controls="mobile-navigation"
            (click)="toggleMobileNav()">
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div class="saas-mobile-title">
            <strong>PERKS Academy</strong>
            <span>Sports Admin</span>
          </div>

          <label class="saas-search">
            <span>CMD K</span>
            <input placeholder="Search athletes, coaches or transactions...">
          </label>

          <div class="saas-user-actions">
            <button type="button" title="Notifications">Alerts</button>
            <button type="button" title="Help">Help</button>
            <div class="saas-divider"></div>
            <div class="saas-user">
              <div>
                <strong>{{ auth.currentUser()?.fullName || 'Super Admin' }}</strong>
                <span>Academy Lead</span>
              </div>
              <button class="saas-avatar" type="button" title="Logout" (click)="logout()">SA</button>
            </div>
          </div>
        </header>

        <section class="saas-content">
          <router-outlet />
        </section>
      </main>

      <nav class="saas-bottom-nav" aria-label="Mobile quick navigation">
        @for (item of mobileNavItems; track item.route) {
          <a
            [routerLink]="item.route"
            routerLinkActive="active"
            [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
            (click)="closeMobileNav()">
            <span><svg aria-hidden="true"><use [attr.href]="'#icon-' + item.icon"></use></svg></span>
            <small>{{ item.label }}</small>
          </a>
        }
      </nav>

      <svg class="icon-sprite" aria-hidden="true">
        <symbol id="icon-dashboard" viewBox="0 0 24 24">
          <path d="M4 13h7V4H4v9Zm0 7h7v-5H4v5Zm9 0h7v-9h-7v9Zm0-16v5h7V4h-7Z" />
        </symbol>
        <symbol id="icon-students" viewBox="0 0 24 24">
          <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3ZM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3Zm0 2c-2.67 0-5 1.34-5 3v2h10v-2c0-1.66-2.33-3-5-3Zm8 0c-.31 0-.62.02-.92.06 1.16.84 1.92 1.94 1.92 3.19V18h4v-2c0-1.66-2.33-3-5-3Z" />
        </symbol>
        <symbol id="icon-guest" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Zm0 2c-3.31 0-7 1.67-7 4v2h14v-2c0-2.33-3.69-4-7-4Zm7-8v3h3v2h-3v3h-2v-3h-3V9h3V6h2Z" />
        </symbol>
        <symbol id="icon-camp" viewBox="0 0 24 24">
          <path d="m12 3 9 17h-6l-3-6-3 6H3L12 3Zm0 5.1L8.35 18h1.42L12 13.55 14.23 18h1.42L12 8.1ZM4 21h16v2H4v-2Z" />
        </symbol>
        <symbol id="icon-attendance" viewBox="0 0 24 24">
          <path d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2Zm11 8H6v10h12V10Zm-1.3 3.3-5.4 5.4-3-3 1.4-1.4 1.6 1.6 4-4 1.4 1.4Z" />
        </symbol>
        <symbol id="icon-fees" viewBox="0 0 24 24">
          <path d="M3 6h18v12H3V6Zm2 2v8h14V8H5Zm7 7c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3Zm-6-5h2v4H6v-4Zm10 0h2v4h-2v-4Z" />
        </symbol>
        <symbol id="icon-staff" viewBox="0 0 24 24">
          <path d="M10 3h4l1 3h5v15H4V6h5l1-3Zm2 9c1.38 0 2.5-1.12 2.5-2.5S13.38 7 12 7 9.5 8.12 9.5 9.5 10.62 12 12 12Zm-5 6h10c-.32-2.24-2.45-4-5-4s-4.68 1.76-5 4Z" />
        </symbol>
        <symbol id="icon-reports" viewBox="0 0 24 24">
          <path d="M5 3h14v18H5V3Zm3 4v2h8V7H8Zm0 4v2h8v-2H8Zm0 4v2h5v-2H8Zm8 1h2v2h-2v-2Z" />
        </symbol>
        <symbol id="icon-settings" viewBox="0 0 24 24">
          <path d="M19.43 12.98c.04-.32.07-.65.07-.98s-.02-.66-.07-.98l2.11-1.65-2-3.46-2.49 1a7.2 7.2 0 0 0-1.7-.98L15 3h-4l-.35 2.93c-.6.23-1.17.56-1.7.98l-2.49-1-2 3.46 2.11 1.65c-.04.32-.07.65-.07.98s.02.66.07.98l-2.11 1.65 2 3.46 2.49-1c.53.42 1.1.75 1.7.98L11 21h4l.35-2.93c.6-.23 1.17-.56 1.7-.98l2.49 1 2-3.46-2.11-1.65ZM13 15.73A4 4 0 1 1 13 8a4 4 0 0 1 0 7.73Z" />
        </symbol>
      </svg>
    </div>
  `
})
export class LayoutComponent {
  readonly today = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date());
  readonly navItems: NavItem[] = [
    { label: 'Dashboard', route: '/dashboard', icon: 'dashboard' },
    { label: 'Students', route: '/students', icon: 'students' },
    { label: 'Guest Play', route: '/guest-booking', icon: 'guest' },
    { label: 'Summer Camp', route: '/summer-camp', icon: 'camp' },
    { label: 'Attendance', route: '/attendance', icon: 'attendance' },
    { label: 'Fees', route: '/fees', icon: 'fees' },
    { label: 'Staff', route: '/staff', icon: 'staff' },
    { label: 'Reports', route: '/reports', icon: 'reports' },
    { label: 'Settings', route: '/settings', icon: 'settings' }
  ];
  readonly mobileNavItems = this.navItems.slice(0, 5);
  isMobileNavOpen = false;

  constructor(readonly auth: AuthService, private readonly router: Router) {}

  toggleMobileNav(): void {
    this.isMobileNavOpen = !this.isMobileNavOpen;
  }

  closeMobileNav(): void {
    this.isMobileNavOpen = false;
  }

  logout(): void {
    this.closeMobileNav();
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}
